import { Button } from '@/components/v1/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/v1/ui/select';
import { isPageCountKnown } from '@/lib/pagination';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import { Label } from '@radix-ui/react-label';
import { Table } from '@tanstack/react-table';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  onSetPageSize?: (pageSize: number) => void;
  showSelectedRows?: boolean;
  isLoading?: boolean;
}

export function DataTablePagination<TData>({
  table,
  onSetPageSize,
  showSelectedRows = true,
  isLoading = false,
}: DataTablePaginationProps<TData>) {
  const pagination = table.getState().pagination;
  const pageCount = table.getPageCount();
  const hasKnownPageCount = isPageCountKnown(pageCount);

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1 text-sm text-gray-600 dark:text-gray-400">
        {showSelectedRows && !isLoading && (
          <div>
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </div>
        )}
      </div>

      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <Label
            className="text-sm font-medium text-gray-600 dark:text-gray-400"
            htmlFor="rows-per-page"
            id="rows-per-page-label"
          >
            Rows per page
          </Label>
          <Select
            value={`${pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
              if (onSetPageSize) {
                onSetPageSize(Number(value));
              }
            }}
          >
            <SelectTrigger
              className="h-8 w-[70px]"
              id="rows-per-page"
              aria-labelledby="rows-per-page-label"
            >
              <SelectValue placeholder={pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[25, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          {isLoading || !hasKnownPageCount
            ? `Page ${pagination.pageIndex + 1}`
            : `Page ${pagination.pageIndex + 1} of ${Math.max(pageCount, 1)}`}
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="outline"
            size="sm"
            className="hidden lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={isLoading || !table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <DoubleArrowLeftIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={isLoading || !table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={isLoading || !table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="hidden lg:flex"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={
              isLoading || !hasKnownPageCount || !table.getCanNextPage()
            }
          >
            <span className="sr-only">Go to last page</span>
            <DoubleArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
